import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { apiUrl, authApi } from "../shared/api.ts";
import { useAuth } from "../shared/auth-context.tsx";
import { AuthLayout } from "../components/AuthLayout.tsx";

/**
 * Operator sign-in. Primary path is email + password (/api/auth/login).
 * The magic-link request (/api/auth/request-link) stays as a secondary
 * affordance — "email me a sign-in link instead" — and doubles as the
 * password-reset path. Both coexist on one screen.
 *
 * On a successful password login the server sets the HttpOnly cspro_session
 * cookie and echoes { tenant_id, email }; we establish the client session the
 * SAME way the magic-link callback does — useAuth().setSession(...) — then
 * navigate to ?next (default /playground), where <Protected> trusts the hint
 * and the cookie carries every subsequent API call.
 */
/**
 * Single sign-on, from the visitor's side.
 *
 * Two ways in. An owner hands their team a link carrying `?sso=<workspace>`,
 * which turns this page into one button. Someone who arrived here the ordinary
 * way can still get there by naming their workspace.
 *
 * The workspace name goes into a URL, so it is validated here against the same
 * shape the server accepts rather than trusted from the query string.
 */
const SSO_SLUG = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/**
 * What a failed sign-in says. The server sends a short fixed code and nothing
 * else -- deliberately, since anything more detailed would be describing
 * another company's identity provider to whoever holds the link. An unknown
 * code falls back to the general line rather than being rendered raw.
 */
const SSO_ERRORS: Record<string, string> = {
  unknown_workspace:
    "We don't recognise that workspace, or single sign-on isn't switched on for it. "
    + "Check the link your admin gave you.",
  provider_refused:
    "Your company's sign-in provider turned down the request. Your IT team can tell you why.",
  expired: "That sign-in attempt timed out. Try again.",
  domain_not_allowed:
    "That account isn't on your workspace's list of allowed email domains.",
  not_a_member:
    "You're signed in with your company account, but you don't have a seat in this workspace yet. "
    + "Ask an owner to invite you.",
  seat_limit:
    "This workspace has no seats left. An owner can free one up or add more.",
  no_email:
    "Your company's sign-in provider didn't give us a verified email address for you.",
  rate_limited: "Too many sign-in attempts just now. Wait a minute and try again.",
  unavailable: "We couldn't complete that sign-in. Try again in a moment.",
  sign_in_failed: "We couldn't complete that sign-in. Try again, or use your email and password.",
};

export function SignInPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const next = searchParams.get("next") || "/playground";
  // Only show the plan hint for known plans — the ?plan param is user-controlled
  // and must not be rendered verbatim as pricing/marketing copy.
  const rawPlan = searchParams.get("plan");
  const planLabel = rawPlan === "starter" || rawPlan === "growth" ? rawPlan : null;

  // Only navigate to same-app paths (no open-redirect), mirroring the callback.
  const safeNext =
    next.startsWith("/") && !next.startsWith("//") ? next : "/playground";

  // Mode: password login (default) vs. magic-link request.
  const [mode, setMode] = useState<"password" | "magic">("password");
  // True when the user arrived in magic mode via "Forgot your password?" —
  // shows an extra helper line explaining how to set a new password.
  const [resetHint, setResetHint] = useState(false);

  // Shared email field.
  const [email, setEmail] = useState("");

  // Password-login state.
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Single sign-on. `?sso=` names the workspace; `?sso_error=` is how a
  // failed round-trip gets back here, since the callback is a redirect and
  // has nowhere else to put a message.
  const ssoParam = searchParams.get("sso") || "";
  const ssoSlug = SSO_SLUG.test(ssoParam) ? ssoParam : "";
  const ssoErrorCode = searchParams.get("sso_error") || "";
  const ssoError = ssoErrorCode
    ? SSO_ERRORS[ssoErrorCode] || SSO_ERRORS.sign_in_failed
    : null;
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoWorkspace, setSsoWorkspace] = useState("");

  const startSso = (slug: string) => {
    const normalized = slug.trim().toLowerCase();
    if (!SSO_SLUG.test(normalized)) return;
    // A full navigation, not a fetch: the provider answers with a redirect
    // and lands the visitor back on the callback with a session cookie.
    window.location.assign(apiUrl(`/api/auth/sso/${encodeURIComponent(normalized)}/start`));
  };

  // Magic-link state (preserved behavior from the original page).
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const onLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoginError(null);
    setNeedsVerification(false);
    setResent(false);
    setResendError(null);
    setLoggingIn(true);
    try {
      const r = await authApi.login(email.trim(), password);
      if (r.ok && r.email && r.tenant_id) {
        setSession({
          email: r.email,
          tenantId: r.tenant_id,
          signedInAt: Date.now(),
        });
        navigate(safeNext, { replace: true });
        return;
      }
      if (r.needs_verification) {
        setNeedsVerification(true);
        setLoginError(r.error || "Please verify your email.");
      } else {
        setLoginError(r.error || "Invalid email or password.");
      }
    } catch (err) {
      setLoginError(
        err instanceof Error
          ? err.message
          : "Network error. Check connection and retry.",
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const onResend = async () => {
    if (!email.trim()) return;
    setResendError(null);
    setResending(true);
    try {
      await authApi.resendVerification(email.trim());
      setResent(true);
    } catch {
      // The endpoint is always-200 server-side, so reaching here means the
      // request never landed (offline/DNS/restart). Tell the user to retry.
      setResendError("Couldn't send — check your connection and try again.");
    } finally {
      setResending(false);
    }
  };

  const onRequestLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLinkError(null);
    setSubmitting(true);
    try {
      const r = await authApi.requestLink(email.trim());
      if (r.ok) {
        setSent(true);
      } else {
        setLinkError(r.error || "We couldn't send the sign-in link. Try again in a moment.");
      }
    } catch (err) {
      setLinkError(
        err instanceof Error
          ? err.message
          : "Network error. Check connection and retry.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const heading = sent ? (
    <>
      Check your <em>inbox</em>.
    </>
  ) : mode === "magic" ? (
    <>
      Email me a <em>link</em>.
    </>
  ) : (
    <>
      Sign in to your <em>tenant</em>.
    </>
  );

  return (
    <AuthLayout className="signin-page">
      <p className="app-eyebrow">
        <span className="dot" /> Satatam · Operator
      </p>
      <h1 className="app-h1">{heading}</h1>

      {sent ? (
        <p className="app-lede">
          We sent a sign-in link to{" "}
          <strong style={{ color: "var(--ink)" }}>{email}</strong>. Click it
          within 15 minutes. The link works once.
        </p>
      ) : mode === "magic" ? (
        <>
          <p className="app-lede">
            Enter your email. We'll send a one-time sign-in link — handy if you
            forgot your password.{" "}
            {planLabel ? (
              <span style={{ color: "var(--mint-text)" }}>
                You're on your way to <strong>{planLabel}</strong>.
              </span>
            ) : null}
          </p>
          {resetHint ? (
            <p
              style={{
                fontSize: "13px",
                lineHeight: 1.5,
                color: "var(--ink-soft)",
                margin: "-8px 0 0",
              }}
            >
              We'll email you a sign-in link. Once you're in, set a new
              password in Settings.
            </p>
          ) : null}
        </>
      ) : (
        <p className="app-lede">
          Enter your email and password.{" "}
          {planLabel ? (
            <span style={{ color: "var(--mint-text)" }}>
              You're on your way to <strong>{planLabel}</strong>.
            </span>
          ) : null}
        </p>
      )}

      {ssoError ? (
        <p
          role="alert"
          style={{
            fontSize: "13px", lineHeight: 1.5, color: "var(--ink-soft)",
            background: "var(--panel-2)", border: "1px solid var(--hairline)",
            borderRadius: "12px", padding: "11px 13px", margin: "0 0 4px",
          }}
        >
          {ssoError}
        </p>
      ) : null}

      {/* ---- Single sign-on ---- */}
      {!sent && ssoSlug ? (
        <div style={{ margin: "4px 0 18px" }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "100%" }}
            data-action="sso-start"
            onClick={() => startSso(ssoSlug)}
          >
            Continue with your company account
          </button>
          <p style={{ fontSize: "12px", color: "var(--ink-faint)", margin: "8px 0 0", textAlign: "center" }}>
            or sign in below
          </p>
        </div>
      ) : null}

      {/* ---- Password login (primary) ---- */}
      {!sent && mode === "password" && (
        <form onSubmit={onLogin} noValidate>
          <div className="field">
            <label htmlFor="signin-email">Email</label>
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loggingIn}
              placeholder="founder@company.com"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="signin-password">Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="signin-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loggingIn}
                placeholder="••••••••"
                required
                style={{ width: "100%", paddingRight: "64px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--ink-faint)",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "6px 8px",
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {/* Reserve space so an error never shifts the layout. */}
            <span
              className="err"
              role="alert"
              style={{ minHeight: "16px", display: "block" }}
            >
              {loginError || " "}
            </span>
            <button
              type="button"
              onClick={() => {
                setMode("magic");
                setLoginError(null);
                setNeedsVerification(false);
                setResetHint(true);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--ink-soft)",
                fontSize: "13px",
                textAlign: "left",
              }}
            >
              Forgot your password?
            </button>
          </div>

          {needsVerification && (
            <div
              style={{
                marginBottom: "18px",
                fontSize: "13px",
                color: "var(--ink-soft)",
              }}
            >
              {resent ? (
                <span style={{ color: "var(--ink-faint)" }}>
                  Sent — check your inbox.
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={onResend}
                    disabled={resending || !email.trim()}
                  >
                    {resending ? "Sending…" : "Resend verification email"}
                  </button>
                  {resendError ? (
                    <span
                      className="err"
                      role="alert"
                      style={{ display: "block", marginTop: "8px" }}
                    >
                      {resendError}
                    </span>
                  ) : null}
                </>
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loggingIn || !email.trim() || !password}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {loggingIn ? "Signing in…" : "Sign in →"}
          </button>

          <p
            style={{
              marginTop: "18px",
              fontSize: "13px",
              color: "var(--ink-soft)",
              textAlign: "center",
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setMode("magic");
                setLoginError(null);
                setNeedsVerification(false);
                setResetHint(false);
              }}
              style={{ width: "100%", justifyContent: "center" }}
            >
              Email me a sign-in link instead
            </button>
          </p>

          <p
            style={{
              marginTop: "18px",
              fontSize: "13px",
              color: "var(--ink-soft)",
              textAlign: "center",
            }}
          >
            New here?{" "}
            <Link to="/signup" style={{ color: "var(--mint-text)" }}>
              Create an account
            </Link>
          </p>

          <Terms />
        </form>
      )}

      {/* ---- Magic-link request (secondary) ---- */}
      {!sent && mode === "magic" && (
        <form onSubmit={onRequestLink} noValidate>
          <div className="field">
            <label htmlFor="signin-magic-email">Email</label>
            <input
              id="signin-magic-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              placeholder="founder@company.com"
              required
            />
            <span
              className="err"
              role="alert"
              style={{ minHeight: "16px", display: "block" }}
            >
              {linkError || " "}
            </span>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !email.trim()}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submitting ? "Sending link…" : "Send sign-in link →"}
          </button>

          <p
            style={{
              marginTop: "18px",
              fontSize: "13px",
              color: "var(--ink-soft)",
              textAlign: "center",
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setMode("password");
                setLinkError(null);
                setResetHint(false);
              }}
              style={{ width: "100%", justifyContent: "center" }}
            >
              ← Use email and password
            </button>
          </p>

          <Terms />
        </form>
      )}

      {/* ---- Magic-link sent confirmation ---- */}
      {sent && (
        <div style={{ marginTop: "32px" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setSent(false);
              setEmail("");
            }}
            style={{ marginRight: "12px" }}
          >
            Use a different email
          </button>
          <span
            style={{
              fontSize: "12.5px",
              color: "var(--ink-faint)",
            }}
          >
            You'll return to{" "}
            <code style={{ color: "var(--ink-soft)" }}>{safeNext}</code> after
            sign-in.
          </span>
        </div>
      )}

      <p
        style={{
          marginTop: "48px",
          fontSize: "13px",
          color: "var(--ink-faint)",
          textAlign: "center",
        }}
      >
        <a
          href="https://satatam.co/"
          style={{ color: "var(--ink-soft)" }}
        >
          ← Back to the marketing site
        </a>
      </p>

      {!sent && !ssoSlug ? (
        <div style={{ marginTop: "18px", paddingTop: "16px", borderTop: "1px solid var(--hairline)" }}>
          {ssoOpen ? (
            <form
              onSubmit={(ev) => { ev.preventDefault(); startSso(ssoWorkspace); }}
              style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}
            >
              <div className="field" style={{ flex: 1, minWidth: "180px", margin: 0 }}>
                <label htmlFor="sso-workspace">Workspace name</label>
                <input
                  id="sso-workspace"
                  value={ssoWorkspace}
                  onChange={(ev) => setSsoWorkspace(ev.target.value)}
                  placeholder="acme"
                  spellCheck={false}
                  autoComplete="organization"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="btn"
                data-action="sso-start"
                disabled={!SSO_SLUG.test(ssoWorkspace.trim().toLowerCase())}
              >
                Continue
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: 0, background: "none" }}
              onClick={() => setSsoOpen(true)}
            >
              Use your company account instead
            </button>
          )}
        </div>
      ) : null}
    </AuthLayout>
  );
}

function Terms() {
  return (
    <p
      style={{
        marginTop: "24px",
        fontSize: "12.5px",
        color: "var(--ink-faint)",
        fontFamily: "var(--mono)",
        letterSpacing: "0.06em",
      }}
    >
      By continuing you accept the{" "}
      <a
        href="https://satatam.co/terms.html"
        style={{ color: "var(--mint-text)" }}
        target="_blank"
        rel="noopener noreferrer"
      >
        terms
      </a>{" "}
      and{" "}
      <a
        href="https://satatam.co/privacy.html"
        style={{ color: "var(--mint-text)" }}
        target="_blank"
        rel="noopener noreferrer"
      >
        privacy policy
      </a>
      .
    </p>
  );
}
